"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAppBaseExportRuntime = createAppBaseExportRuntime;
// Keep this factory self-contained: the same function is injected into both browser runtimes.
function createAppBaseExportRuntime() {
    const safeFilename = (value, fallback) => {
        const raw = String(value || fallback || 'export').trim();
        const cleaned = raw.replace(/[\\/\u0000\r\n]/g, '_').slice(0, 160);
        return cleaned || fallback || 'export';
    };
    const canvasToBlob = (canvas, mimeType, quality) => new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            if (!blob) {
                reject(new Error('Canvas export failed'));
                return;
            }
            resolve(blob);
        }, mimeType, quality);
    });
    const collectCssText = () => {
        const chunks = [];
        for (const sheet of Array.from(document.styleSheets)) {
            try {
                for (const rule of Array.from(sheet.cssRules || [])) {
                    chunks.push(rule.cssText);
                }
            }
            catch { }
        }
        return chunks.join('\n');
    };
    const syncFormState = (source, clone) => {
        const sourceFields = source.querySelectorAll('input, textarea, select');
        const cloneFields = clone.querySelectorAll('input, textarea, select');
        sourceFields.forEach((field, index) => {
            const target = cloneFields[index];
            if (!target)
                return;
            if (field instanceof HTMLTextAreaElement &&
                target instanceof HTMLTextAreaElement) {
                target.textContent = field.value;
                return;
            }
            if (field instanceof HTMLInputElement &&
                target instanceof HTMLInputElement) {
                if (field.type === 'checkbox' || field.type === 'radio') {
                    if (field.checked)
                        target.setAttribute('checked', 'checked');
                    else
                        target.removeAttribute('checked');
                    return;
                }
                target.setAttribute('value', field.value);
                return;
            }
            if (field instanceof HTMLSelectElement &&
                target instanceof HTMLSelectElement) {
                Array.from(target.options).forEach((option, optionIndex) => {
                    if (field.options[optionIndex]?.selected)
                        option.setAttribute('selected', 'selected');
                    else
                        option.removeAttribute('selected');
                });
            }
        });
    };
    const MAX_EXPORT_PIXELS = 16777216;
    const MAX_EXPORT_PDF_PAGE_AREA = 2000000;
    const assertExportBudget = (width, height, scale) => {
        const pixels = Math.ceil(width * scale) * Math.ceil(height * scale);
        if (!Number.isFinite(pixels) || pixels > MAX_EXPORT_PIXELS) {
            throw new Error('App export exceeds the platform pixel budget');
        }
    };
    const assertPdfPageBudget = (page) => {
        if (!page)
            return;
        const pageWidth = Number(page.width) || 595.28;
        const pageHeight = Number(page.height) || 841.89;
        if (!Number.isFinite(pageWidth) ||
            !Number.isFinite(pageHeight) ||
            pageWidth < 1 ||
            pageHeight < 1 ||
            pageWidth * pageHeight > MAX_EXPORT_PDF_PAGE_AREA) {
            throw new Error('App PDF page exceeds the platform page budget');
        }
    };
    const renderElementToCanvas = async (options) => {
        const selector = String(options.selector || '').trim();
        const element = options.element || (selector ? document.querySelector(selector) : null);
        if (!(element instanceof Element)) {
            throw new Error('Export element not found');
        }
        const rect = element.getBoundingClientRect();
        const width = Math.max(1, Math.round(Number(options.width) || rect.width));
        const height = Math.max(1, Math.round(Number(options.height) || rect.height));
        const scale = Math.min(4, Math.max(1, Number(options.scale) || window.devicePixelRatio || 1));
        assertExportBudget(width, height, scale);
        const cloneNode = element.cloneNode(true);
        syncFormState(element, cloneNode);
        if (cloneNode instanceof HTMLElement) {
            // The size is measured over padding and border, so it is set that way.
            cloneNode.style.boxSizing = 'border-box';
            cloneNode.style.width = width + 'px';
            cloneNode.style.height = height + 'px';
        }
        cloneNode.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
        const cssText = collectCssText();
        const serialized = new XMLSerializer().serializeToString(cloneNode);
        const background = options.background ||
            getComputedStyle(element).backgroundColor ||
            '#ffffff';
        const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' +
            width +
            '" height="' +
            height +
            '" viewBox="0 0 ' +
            width +
            ' ' +
            height +
            '">' +
            '<foreignObject width="100%" height="100%">' +
            '<div xmlns="http://www.w3.org/1999/xhtml" style="width:' +
            width +
            'px;height:' +
            height +
            'px;background:' +
            background +
            ';overflow:hidden;">' +
            '<style>' +
            cssText.replace(/<\//g, '<\\/') +
            '</style>' +
            serialized +
            '</div></foreignObject></svg>';
        const image = new Image();
        image.decoding = 'async';
        const loaded = new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = () => reject(new Error('Export image render failed'));
        });
        // A data: address, not a blob: one: Chromium marks a canvas that drew an
        // SVG with foreignObject from a blob: address as unreadable, and the
        // export then fails.
        image.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
        await loaded;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const context = canvas.getContext('2d');
        if (!context)
            throw new Error('Canvas context unavailable');
        context.fillStyle = background;
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.scale(scale, scale);
        context.drawImage(image, 0, 0, width, height);
        return canvas;
    };
    const binaryStringToBytes = (binary) => {
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index += 1) {
            bytes[index] = binary.charCodeAt(index) & 255;
        }
        return bytes;
    };
    const textToBytes = (text) => new TextEncoder().encode(text);
    const buildPdfFromJpeg = (jpegDataUrl, imageWidth, imageHeight, page) => {
        assertPdfPageBudget(page);
        const pageWidth = Number(page?.width) || 595.28;
        const pageHeight = Number(page?.height) || 841.89;
        const margin = Math.max(0, Number(page?.margin) || 0);
        const maxWidth = Math.max(1, pageWidth - margin * 2);
        const maxHeight = Math.max(1, pageHeight - margin * 2);
        const ratio = Math.min(maxWidth / imageWidth, maxHeight / imageHeight);
        const drawWidth = imageWidth * ratio;
        const drawHeight = imageHeight * ratio;
        const drawX = (pageWidth - drawWidth) / 2;
        const drawY = (pageHeight - drawHeight) / 2;
        const imageBytes = binaryStringToBytes(atob(jpegDataUrl.split(',')[1] || ''));
        const chunks = [];
        const offsets = [0];
        let offset = 0;
        const pushBytes = (bytes) => {
            chunks.push(bytes);
            offset += bytes.byteLength;
        };
        const pushText = (text) => pushBytes(new Uint8Array(textToBytes(text)));
        const objectStart = (objectNumber) => {
            offsets[objectNumber] = offset;
            pushText(String(objectNumber) + ' 0 obj\n');
        };
        pushText('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');
        objectStart(1);
        pushText('<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');
        objectStart(2);
        pushText('<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');
        objectStart(3);
        pushText('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' +
            pageWidth +
            ' ' +
            pageHeight +
            '] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>\nendobj\n');
        objectStart(4);
        pushText('<< /Type /XObject /Subtype /Image /Width ' +
            imageWidth +
            ' /Height ' +
            imageHeight +
            ' /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ' +
            imageBytes.byteLength +
            ' >>\nstream\n');
        pushBytes(imageBytes);
        pushText('\nendstream\nendobj\n');
        const content = 'q\n' +
            drawWidth +
            ' 0 0 ' +
            drawHeight +
            ' ' +
            drawX +
            ' ' +
            drawY +
            ' cm\n/Im0 Do\nQ\n';
        objectStart(5);
        pushText('<< /Length ' +
            textToBytes(content).byteLength +
            ' >>\nstream\n' +
            content +
            'endstream\nendobj\n');
        const xrefOffset = offset;
        pushText('xref\n0 6\n0000000000 65535 f \n');
        for (let objectNumber = 1; objectNumber <= 5; objectNumber += 1) {
            pushText(String(offsets[objectNumber]).padStart(10, '0') + ' 00000 n \n');
        }
        pushText('trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n' + xrefOffset + '\n%%EOF');
        return new Blob(chunks, { type: 'application/pdf' });
    };
    const normalizeExportFormat = (value) => {
        if (value === 'jpg')
            return 'jpeg';
        if (value === 'png' || value === 'jpeg' || value === 'pdf')
            return value;
        throw new Error('Unsupported export format');
    };
    const exportElement = async (options) => {
        const format = normalizeExportFormat(options.format || 'png');
        const canvas = await renderElementToCanvas(options);
        const filenameBase = safeFilename(options.filename, 'export');
        if (format === 'pdf') {
            const jpegDataUrl = canvas.toDataURL('image/jpeg', Number(options.quality) || 0.92);
            const blob = buildPdfFromJpeg(jpegDataUrl, canvas.width, canvas.height, options.page);
            return Object.freeze({
                format,
                mimeType: 'application/pdf',
                filename: filenameBase.endsWith('.pdf')
                    ? filenameBase
                    : filenameBase + '.pdf',
                bytes: blob.size,
                blob,
            });
        }
        const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
        const blob = await canvasToBlob(canvas, mimeType, Number(options.quality) || 0.92);
        const extension = format === 'jpeg' ? '.jpg' : '.png';
        return Object.freeze({
            format,
            mimeType,
            filename: /\.(png|jpe?g)$/i.test(filenameBase)
                ? filenameBase
                : filenameBase + extension,
            bytes: blob.size,
            blob,
        });
    };
    const downloadExport = (exported, filename) => {
        if (!exported?.blob)
            throw new Error('Export blob is required');
        const url = URL.createObjectURL(exported.blob);
        try {
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = safeFilename(filename || exported.filename, 'export');
            anchor.rel = 'noopener';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            return {
                success: true,
                filename: anchor.download,
                bytes: exported.blob.size,
            };
        }
        finally {
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
    };
    return { exportElement, downloadExport };
}
